import {
  forwardRef,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { parseQuantity, type QuantityUnit } from '@kk-pos/shared/purchasing';
import { cn } from '@/lib/cn';
import { money, parseMoney } from '@/lib/format';

/* Shared control chrome. One definition so an <input>, a <select> and a
   custom trigger are visually indistinguishable in a form row. */
const CONTROL = [
  'w-full bg-surface-sunken text-ink placeholder:text-ink-faint',
  'border border-line rounded',
  'transition-[border-color,background-color] duration-100',
  'hover:border-line-strong',
  'focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'aria-[invalid=true]:border-negative aria-[invalid=true]:ring-negative',
].join(' ');

const CONTROL_SIZES = {
  sm: 'h-7 px-2 text-xs',
  md: 'h-8 px-2.5 text-sm',
  lg: 'h-10 px-3 text-base',
} as const;

export type ControlSize = keyof typeof CONTROL_SIZES;

/* ── Field wrapper ────────────────────────────────────────────────────────── */

export interface FieldProps {
  label?: ReactNode;
  /** Explanatory text under the control. Replaced by `error` when set. */
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Right-aligned adornment on the label row (e.g. a "reset" link). */
  action?: ReactNode;
  className?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({
  label,
  hint,
  error,
  required,
  action,
  className,
  children,
  htmlFor,
}: FieldProps) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      {label || action ? (
        <div className="flex items-baseline gap-2">
          {label ? (
            <label
              htmlFor={htmlFor}
              className="text-2xs text-ink-faint font-medium tracking-wider uppercase"
            >
              {label}
              {required ? <span className="text-negative ml-0.5">*</span> : null}
            </label>
          ) : null}
          {action ? <div className="ml-auto">{action}</div> : null}
        </div>
      ) : null}
      {children}
      {error ? (
        <p className="text-2xs text-negative">{error}</p>
      ) : hint ? (
        <p className="text-2xs text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}

/* ── Text input ───────────────────────────────────────────────────────────── */

// `prefix` is omitted from the DOM attributes on purpose: HTMLAttributes types
// it as `string` (the RDFa attribute), which collides with the ReactNode
// adornment we want. `size` likewise means "character width" on a DOM input.
export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix'
> {
  size?: ControlSize;
  invalid?: boolean;
  /** Rendered inside the control on the leading edge. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Force monospace + tabular figures (ids, barcodes, quantities). */
  mono?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', invalid, prefix, suffix, mono, className, ...props },
  ref
) {
  const input = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL,
        CONTROL_SIZES[size],
        mono && 'font-mono tabular-nums',
        prefix && 'pl-7',
        suffix && 'pr-7',
        className
      )}
      {...props}
    />
  );
  if (!prefix && !suffix) return input;
  return (
    <div className="relative flex min-w-0 items-center">
      {prefix ? (
        <span className="text-ink-faint pointer-events-none absolute left-2 flex items-center [&_svg]:size-3.5">
          {prefix}
        </span>
      ) : null}
      {input}
      {suffix ? (
        <span className="text-ink-faint absolute right-2 flex items-center [&_svg]:size-3.5">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

/* ── Money input ──────────────────────────────────────────────────────────────
   Holds a *string* while focused so the operator can type freely, and commits
   integer cents on blur. Never round-trips through a float.                   */

export function MoneyInput({
  value,
  onValueChange,
  size = 'md',
  invalid,
  className,
  allowNegative = false,
  ...props
}: Omit<InputProps, 'value' | 'onChange' | 'prefix'> & {
  /** Integer cents, or null for empty. */
  value: number | null;
  onValueChange: (cents: number | null) => void;
  allowNegative?: boolean;
}) {
  return (
    <Input
      {...props}
      size={size}
      invalid={invalid}
      mono
      inputMode="decimal"
      prefix={<span className="font-mono text-xs">$</span>}
      className={cn('text-right', className)}
      defaultValue={value === null ? '' : (value / 100).toFixed(2)}
      key={value ?? 'empty'}
      onBlur={(e) => {
        const parsed = parseMoney(e.currentTarget.value);
        const next = parsed === null ? null : allowNegative ? parsed : Math.abs(parsed);
        e.currentTarget.value = next === null ? '' : (next / 100).toFixed(2);
        onValueChange(next);
        props.onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        props.onKeyDown?.(e);
      }}
      title={value === null ? undefined : money(value)}
    />
  );
}

/* ── Quantity input ───────────────────────────────────────────────────────────
   A purchase quantity box (TASK-0228). The caller keeps the RAW TEXT, and the
   box shows exactly that text: it is never rebuilt from a number, so "0." keeps
   its point while the operator types 0.5, and a cleared box stays blank instead
   of turning into 0. Number() is never called per keystroke; the text is read
   with `parseQuantity` (`@kk-pos/shared/purchasing`) on blur and Enter, and the
   screen converts it once more when it builds its request.

   `ea` and `pack` take whole numbers, `lb` up to 3 decimals. Other text is
   left as typed (never stripped or rewritten, so "1.5" in an each box cannot
   quietly become 15), marked aria-invalid, and commits null.               */

export interface QuantityInputProps extends Omit<
  InputProps,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'inputMode' | 'mono'
> {
  /** The raw text, as typed. Keep it as a string in screen state. */
  value: string;
  unit: QuantityUnit;
  onValueChange: (text: string) => void;
  /** On blur and on Enter: the quantity, or null when blank or not readable. */
  onCommit?: (quantity: number | null) => void;
  /**
   * Enter, after `onCommit`. When given, Enter is the caller's (a grid moves
   * focus) and no longer submits a surrounding form.
   */
  onEnter?: (quantity: number | null) => void;
  /**
   * Select the whole box when it takes focus (a click, a Tab or a programmatic
   * focus), so the next key replaces the "1" the line started with. The
   * browser's own mouse-up would otherwise drop the selection again.
   */
  selectOnFocus?: boolean;
}

export const QuantityInput = forwardRef<HTMLInputElement, QuantityInputProps>(
  function QuantityInput(
    {
      value,
      unit,
      onValueChange,
      onCommit,
      onEnter,
      selectOnFocus,
      invalid,
      className,
      onBlur,
      onFocus,
      onMouseUp,
      onKeyDown,
      ...props
    },
    ref
  ) {
    const unreadable = value.trim() !== '' && parseQuantity(value, unit) === null;
    const justFocused = useRef(false);
    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        mono
        inputMode={unit === 'lb' ? 'decimal' : 'numeric'}
        autoComplete="off"
        spellCheck={false}
        value={value}
        invalid={invalid || unreadable}
        className={cn('text-right', className)}
        onChange={(e) => onValueChange(e.currentTarget.value)}
        onFocus={(e) => {
          if (selectOnFocus) {
            justFocused.current = true;
            e.currentTarget.select();
          }
          onFocus?.(e);
        }}
        onMouseUp={(e) => {
          // The mouse-up that ends the click which focused the box would put
          // the caret back; keep the selection once.
          if (justFocused.current) e.preventDefault();
          justFocused.current = false;
          onMouseUp?.(e);
        }}
        onBlur={(e) => {
          justFocused.current = false;
          onCommit?.(parseQuantity(e.currentTarget.value, unit));
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented || e.key !== 'Enter') return;
          const quantity = parseQuantity(e.currentTarget.value, unit);
          onCommit?.(quantity);
          if (onEnter) {
            e.preventDefault();
            onEnter(quantity);
          }
        }}
      />
    );
  }
);

/* ── Search input ─────────────────────────────────────────────────────────── */

export const SearchInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'value' | 'onChange' | 'prefix' | 'suffix'> & {
    value: string;
    onValueChange: (value: string) => void;
    /** Keyboard hint shown while empty and unfocused, e.g. `/`. */
    kbd?: string;
  }
>(function SearchInput(
  {
    value,
    onValueChange,
    placeholder = 'Search…',
    size = 'md',
    className,
    autoFocus,
    kbd,
    ...props
  },
  ref
) {
  return (
    <Input
      {...props}
      ref={ref}
      size={size}
      autoFocus={autoFocus}
      value={value}
      placeholder={placeholder}
      prefix={<Search />}
      className={className}
      onChange={(e) => onValueChange(e.currentTarget.value)}
      onKeyDown={(e) => {
        // The caller runs first so a list can own arrow keys and Enter.
        props.onKeyDown?.(e);
        if (e.defaultPrevented) return;
        // Escape clears before it closes anything above — a search box that
        // eats one Escape then closes on the second is the expected feel.
        if (e.key === 'Escape' && value) {
          e.stopPropagation();
          onValueChange('');
        }
      }}
      suffix={
        value ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear search"
            className="hover:text-ink pointer-events-auto -mr-1 rounded-xs p-0.5"
            onClick={() => onValueChange('')}
          >
            <X className="size-3.5" />
          </button>
        ) : kbd ? (
          <kbd className="border-line text-2xs text-ink-faint rounded-xs border px-1 font-mono">
            {kbd}
          </kbd>
        ) : null
      }
    />
  );
});

/* ── Select ───────────────────────────────────────────────────────────────── */

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  size?: ControlSize;
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid, className, children, ...props },
  ref
) {
  return (
    <div className="relative flex min-w-0 items-center">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          CONTROL,
          CONTROL_SIZES[size],
          'cursor-pointer appearance-none pr-7',
          className
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="text-ink-faint pointer-events-none absolute right-2 size-3.5" />
    </div>
  );
});

/* ── Textarea ─────────────────────────────────────────────────────────────── */

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL, 'resize-y px-2.5 py-1.5 text-sm leading-relaxed', className)}
      {...props}
    />
  );
});

/* ── Checkbox / Switch ────────────────────────────────────────────────────── */

export function Checkbox({
  checked,
  indeterminate = false,
  onCheckedChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const on = checked || indeterminate;
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <button
        id={id}
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-xs border transition-colors duration-100',
          on
            ? 'border-signal bg-signal text-ink-inverse'
            : 'border-line-strong bg-surface-sunken hover:border-ink-faint',
          disabled && 'cursor-not-allowed opacity-40'
        )}
      >
        {indeterminate ? (
          <span className="h-px w-2 bg-current" />
        ) : checked ? (
          <Check className="size-3 stroke-[3]" />
        ) : null}
      </button>
      {label ? (
        <label htmlFor={id} className="text-ink cursor-pointer text-sm select-none">
          {label}
        </label>
      ) : null}
    </div>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label className={cn('flex cursor-pointer items-center gap-2', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          'relative h-4 w-7 shrink-0 rounded-full border transition-colors duration-150',
          checked ? 'border-signal bg-signal' : 'border-line-strong bg-surface-sunken',
          disabled && 'cursor-not-allowed opacity-40'
        )}
      >
        <span
          className={cn(
            'absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full transition-[left] duration-150',
            checked ? 'bg-ink-inverse left-3.5' : 'bg-ink-faint left-0.5'
          )}
        />
      </button>
      {label ? <span className="text-sm select-none">{label}</span> : null}
    </label>
  );
}

/** Horizontal label/control pair — the settings-page row. */
export function FieldRow({
  label,
  hint,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'border-line-soft flex items-center justify-between gap-6 border-b py-3 last:border-0',
        className
      )}
    >
      <div className="min-w-0">
        <div className="text-ink text-sm">{label}</div>
        {hint ? <div className="text-2xs text-ink-faint mt-0.5">{hint}</div> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}
